import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { logger } from '../lib/logger';
import { toast } from 'sonner';
import {
    Gift,
    TrendingUp,
    Clock,
    CheckCircle2,
    XCircle,
    Search,
    Loader2,
    DollarSign,
    ArrowUpDown,
    Ban,
    Activity,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';

const API_BASE = window.location.origin;

export default function VoucherDashboard() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [vouchers, setVouchers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterLocation, setFilterLocation] = useState('all');
    const [filterEmailStatus, setFilterEmailStatus] = useState('all');
    const [sortDir, setSortDir] = useState('desc'); // desc = newest first

    // Redeem inline
    const [redeemCode, setRedeemCode] = useState('');
    const [redeemPin, setRedeemPin] = useState('');
    const [redeemStep, setRedeemStep] = useState('idle');
    const [redeemResult, setRedeemResult] = useState(null);

    // Void dialog
    const [voidTarget, setVoidTarget] = useState(null);
    const [voiding, setVoiding] = useState(false);

    // v3.4.0 — Delivery event timeline modal
    const [timelineTarget, setTimelineTarget] = useState(null);
    const [timelineData, setTimelineData] = useState(null);
    const [timelineLoading, setTimelineLoading] = useState(false);

    const token = localStorage.getItem('yulontap_token');

    const STATUS_CONFIG = {
        active:   { label: t('vouchers.statusActive'),  className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' },
        redeemed: { label: t('vouchers.statusUsed'),    className: 'bg-red-500/10 text-red-600 border-red-500/30' },
        expired:  { label: t('vouchers.statusExpired'),  className: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
        voided:   { label: t('vouchers.statusVoided'),   className: 'bg-slate-500/10 text-slate-500 border-slate-500/30' },
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${token}` };
            const [listRes, reportRes] = await Promise.all([
                fetch(`${API_BASE}/api/vouchers/list`, { headers }),
                fetch(`${API_BASE}/api/vouchers/report`, { headers }),
            ]);
            if (listRes.ok) setVouchers(await listRes.json());
            if (reportRes.ok) setSummary(await reportRes.json());
        } catch (e) {
            console.error('Failed to load voucher data', e);
            toast.error(t('vouchers.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [token, t]);

    useEffect(() => { loadData(); }, [loadData]);

    // v3.4.0 — Fetch delivery event timeline for a single voucher.
    const openTimeline = async (code) => {
        setTimelineTarget(code);
        setTimelineData(null);
        setTimelineLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const res = await fetch(`${API_BASE}/api/vouchers/${encodeURIComponent(code)}/delivery-events`, { headers });
            if (res.ok) {
                setTimelineData(await res.json());
            } else {
                toast.error(t('vouchers.timelineFailed', 'Failed to load delivery timeline'));
                setTimelineTarget(null);
            }
        } catch (e) {
            console.error('Timeline fetch failed', e);
            toast.error(t('vouchers.timelineFailed', 'Failed to load delivery timeline'));
            setTimelineTarget(null);
        } finally {
            setTimelineLoading(false);
        }
    };

    // Unique locations from data
    const locations = [...new Set(vouchers.map(v => v.location_name).filter(Boolean))];

    // Filtering
    const filteredVouchers = vouchers.filter(v => {
        if (filterStatus !== 'all' && v.status !== filterStatus) return false;
        if (filterLocation !== 'all' && v.location_name !== filterLocation) return false;
        if (filterEmailStatus !== 'all') {
            if (filterEmailStatus === 'none') {
                if (v.delivery_email) return false;
            } else if (v.email_send_status !== filterEmailStatus) {
                return false;
            }
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                v.code.toLowerCase().includes(term) ||
                (v.issued_by_name || '').toLowerCase().includes(term) ||
                (v.redeemed_by_name || '').toLowerCase().includes(term) ||
                (v.location_name || '').toLowerCase().includes(term) ||
                (v.reason_label || '').toLowerCase().includes(term) ||
                (v.delivery_email || '').toLowerCase().includes(term)
            );
        }
        return true;
    });

    // Sorting by date
    const sortedVouchers = [...filteredVouchers].sort((a, b) => {
        const da = parseUtc(a.issued_at).getTime();
        const db = parseUtc(b.issued_at).getTime();
        return sortDir === 'desc' ? db - da : da - db;
    });

    const toggleSort = () => setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');

    const formatDate = (ts) => {
        if (!ts) return '-';
        return parseUtc(ts).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    // === Redeem Handler ===
    const handleRedeem = async () => {
        if (!redeemCode || redeemPin.length !== 4) return;
        setRedeemStep('redeeming');
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: redeemCode.toUpperCase().trim(), pin: redeemPin }),
            });
            let data = {};
            try {
                data = await res.json();
            } catch (parseErr) {
                logger.warn('[VoucherDashboard] non-JSON response from /api/vouchers/redeem', parseErr);
            }
            if (res.ok) {
                setRedeemResult({ ok: true, message: t('vouchers.redeemSuccessToast', { code: data.code, value: data.value }) });
                toast.success(t('vouchers.voucherRedeemedToast', { code: data.code }));
            } else {
                setRedeemResult({ ok: false, message: translateBackendError(stringifyDetail(data.detail), t) || t('vouchers.failed') });
            }
            setRedeemStep('result');
            loadData();
        } catch {
            setRedeemResult({ ok: false, message: t('vouchers.connectionError') });
            setRedeemStep('result');
        }
    };

    // === Void Handler ===
    const confirmVoid = async () => {
        if (!voidTarget) return;
        setVoiding(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/${voidTarget}/void`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                toast.success(t('vouchers.voucherVoidedToast', { code: voidTarget }));
                loadData();
            } else {
                const data = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(data.detail) || t('vouchers.voidFailed'));
            }
        } catch {
            toast.error(t('vouchers.connectionError'));
        } finally {
            setVoiding(false);
            setVoidTarget(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="voucher-dashboard-loading">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="voucher-dashboard">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Gift className="w-6 h-6 text-emerald-500" />
                    {t('vouchers.dashboardTitle')}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {t('vouchers.dashboardSubtitle')}
                </p>
            </div>

            {/* Summary Metrics */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card data-insight-id="vouchers.kpi.total-issued">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <Gift className="w-4 h-4 text-emerald-500" />
                                {t('vouchers.totalIssued')}
                            </div>
                            <div className="text-2xl font-bold text-foreground" data-testid="voucher-stat-issued">
                                {summary.total_issued}
                            </div>
                            <div className="text-xs text-muted-foreground">${(summary.issued_value || 0).toFixed(0)} {t('vouchers.valueLabel')}</div>
                        </CardContent>
                    </Card>
                    <Card data-insight-id="vouchers.kpi.redeemed">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <CheckCircle2 className="w-4 h-4 text-red-500" />
                                {t('vouchers.redeemed')}
                            </div>
                            <div className="text-2xl font-bold text-foreground" data-testid="voucher-stat-redeemed">
                                {summary.total_redeemed}
                            </div>
                            <div className="text-xs text-muted-foreground">${(summary.redeemed_value || 0).toFixed(0)} {t('vouchers.valueLabel')}</div>
                        </CardContent>
                    </Card>
                    <Card data-insight-id="vouchers.kpi.outstanding" data-insight-primary="true">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <Clock className="w-4 h-4 text-orange-500" />
                                {t('vouchers.outstanding')}
                            </div>
                            <div className="text-2xl font-bold text-foreground" data-testid="voucher-stat-outstanding">
                                {summary.total_active}
                            </div>
                            <div className="text-xs text-muted-foreground">${(summary.outstanding_value || 0).toFixed(0)} {t('vouchers.valueLabel')}</div>
                        </CardContent>
                    </Card>
                    <Card data-insight-id="vouchers.kpi.redemption-rate">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <TrendingUp className="w-4 h-4 text-violet-500" />
                                {t('vouchers.redemptionRate')}
                            </div>
                            <div className="text-2xl font-bold text-foreground" data-testid="voucher-stat-rate">
                                {summary.redemption_rate}%
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {summary.top_reason ? t('vouchers.topReason', { reason: summary.top_reason.label }) : '-'}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Category Breakdown (Recovery / Recognition / VIP) */}
            {summary?.category_breakdown && (
                <div className="grid grid-cols-3 gap-3" data-testid="category-breakdown" data-insight-id="vouchers.kpi.category-breakdown">
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-sm mb-1">
                                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                <span className="text-muted-foreground">{t('vouchers.categoryRecovery', 'Recovery')}</span>
                            </div>
                            <div className="text-xl font-bold text-foreground" data-testid="stat-recovery-count">
                                {summary.category_breakdown.recovery?.count || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                ${summary.category_breakdown.recovery?.value?.toFixed(0) || 0}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-sm mb-1">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                <span className="text-muted-foreground">{t('vouchers.categoryRecognition', 'Recognition')}</span>
                            </div>
                            <div className="text-xl font-bold text-foreground" data-testid="stat-recognition-count">
                                {summary.category_breakdown.recognition?.count || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                ${summary.category_breakdown.recognition?.value?.toFixed(0) || 0}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-sm mb-1">
                                <span className="w-2.5 h-2.5 rounded-full gold-gradient-dot" />
                                <span className="text-muted-foreground">{t('vouchers.categoryVip', 'VIP')}</span>
                            </div>
                            <div className="text-xl font-bold text-foreground" data-testid="stat-vip-count">
                                {summary.category_breakdown.vip?.count || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                ${summary.category_breakdown.vip?.value?.toFixed(0) || 0}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Quick Redeem */}
            <Card data-insight-id="vouchers.action.quick-redeem">
                <CardContent className="pt-4">
                    <h3 className="font-semibold text-foreground text-sm mb-3">{t('vouchers.quickRedeem')}</h3>
                    <div className="flex gap-2 items-end flex-wrap">
                        <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">{t('vouchers.voucherCode')}</label>
                            <Input
                                data-testid="dashboard-redeem-code"
                                value={redeemCode}
                                onChange={(e) => { setRedeemCode(e.target.value.toUpperCase()); setRedeemStep('idle'); setRedeemResult(null); }}
                                placeholder="RV-A7K3M9Q"
                                className="w-40 font-mono text-sm"
                            />
                        </div>
                        {redeemStep === 'idle' && redeemCode.length >= 6 && (
                            <Button data-testid="dashboard-redeem-next" size="sm" onClick={() => setRedeemStep('pin')}>
                                <DollarSign className="w-3 h-3 mr-1" /> {t('vouchers.redeem')}
                            </Button>
                        )}
                        {redeemStep === 'pin' && (
                            <>
                                <div className="space-y-1" data-insight-id="vouchers.redeem.pin-step">
                                    <label className="text-xs text-muted-foreground">{t('vouchers.staffPin')}</label>
                                    <Input
                                        data-testid="dashboard-redeem-pin"
                                        type="password"
                                        inputMode="numeric"
                                        value={redeemPin}
                                        onChange={(e) => setRedeemPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                        onKeyDown={(e) => e.key === 'Enter' && redeemPin.length === 4 && handleRedeem()}
                                        placeholder="PIN"
                                        className="w-24 text-center"
                                        maxLength={4}
                                        autoFocus
                                    />
                                </div>
                                <Button data-testid="dashboard-redeem-confirm" size="sm" onClick={handleRedeem} disabled={redeemPin.length !== 4}>
                                    {t('common.confirm')}
                                </Button>
                            </>
                        )}
                        {redeemStep === 'redeeming' && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
                        {redeemStep === 'result' && redeemResult && (
                            <p className={`text-sm font-medium ${redeemResult.ok ? 'text-emerald-500' : 'text-red-400'}`}>
                                {redeemResult.message}
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder={t('vouchers.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="voucher-search"
                        data-insight-id="vouchers.action.customer-lookup"
                        data-insight-label="Customer / voucher lookup"
                    />
                </div>
                {locations.length > 1 && (
                    <Select value={filterLocation} onValueChange={setFilterLocation}>
                        <SelectTrigger className="w-[160px]" data-testid="voucher-filter-location">
                            <SelectValue placeholder={t('vouchers.location')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('vouchers.allLocations')}</SelectItem>
                            {locations.map(loc => (
                                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[140px]" data-testid="voucher-filter-status">
                        <SelectValue placeholder={t('vouchers.status')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('vouchers.allStatuses')}</SelectItem>
                        <SelectItem value="active">{t('vouchers.statusActive')}</SelectItem>
                        <SelectItem value="redeemed">{t('vouchers.statusUsed')}</SelectItem>
                        <SelectItem value="expired">{t('vouchers.statusExpired')}</SelectItem>
                        <SelectItem value="voided">{t('vouchers.statusVoided')}</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterEmailStatus} onValueChange={setFilterEmailStatus}>
                    <SelectTrigger className="w-[160px]" data-testid="voucher-filter-email-status">
                        <SelectValue placeholder={t('vouchers.emailStatus', 'Email status')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('vouchers.emailAll', 'All deliveries')}</SelectItem>
                        <SelectItem value="sent">{t('vouchers.emailSent', 'Sent')}</SelectItem>
                        <SelectItem value="pending">{t('vouchers.emailPending', 'Pending')}</SelectItem>
                        <SelectItem value="failed">{t('vouchers.emailFailed', 'Failed')}</SelectItem>
                        <SelectItem value="none">{t('vouchers.emailNone', 'No email')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Master Table */}
            <Card>
                <CardContent className="p-0">
                    {sortedVouchers.length === 0 ? (
                        <div className="text-center py-12 px-4">
                            <Gift className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                            <h3 className="text-lg font-medium text-foreground mb-1">{t('vouchers.noVouchersFound')}</h3>
                            <p className="text-sm text-muted-foreground max-w-md mx-auto">
                                {vouchers.length === 0
                                    ? t('vouchers.noVouchersHint')
                                    : t('vouchers.noVouchersFilter')}
                            </p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        <button
                                            onClick={toggleSort}
                                            className="flex items-center gap-1 hover:text-foreground transition-colors"
                                            data-testid="voucher-sort-date"
                                        >
                                            {t('vouchers.date')}
                                            <ArrowUpDown className="w-3 h-3" />
                                        </button>
                                    </TableHead>
                                    <TableHead>{t('vouchers.location')}</TableHead>
                                    <TableHead>{t('vouchers.voucherNumber')}</TableHead>
                                    <TableHead>{t('vouchers.amount')}</TableHead>
                                    <TableHead>{t('vouchers.reason')}</TableHead>
                                    <TableHead>{t('vouchers.issuedBy')}</TableHead>
                                    <TableHead data-insight-id="vouchers.column.delivery-status">{t('vouchers.delivery')}</TableHead>
                                    <TableHead>{t('vouchers.redeemedBy')}</TableHead>
                                    <TableHead>{t('vouchers.status')}</TableHead>
                                    <TableHead className="w-28">{t('vouchers.actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedVouchers.map(v => {
                                    const cfg = STATUS_CONFIG[v.status] || STATUS_CONFIG.active;
                                    return (
                                        <TableRow key={v.code} data-testid={`voucher-row-${v.code}`}>
                                            <TableCell className="text-sm whitespace-nowrap">
                                                {formatDate(v.issued_at)}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {v.location_name || '-'}
                                            </TableCell>
                                            <TableCell className="font-mono font-medium text-foreground text-sm">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`w-2 h-2 rounded-full shrink-0 ${
                                                        v.stripe_color === 'red' ? 'bg-red-500'
                                                        : v.stripe_color === 'gold' ? 'gold-gradient-dot'
                                                        : v.stripe_color === 'blue' ? 'bg-blue-500'
                                                        : 'bg-emerald-500'
                                                    }`} />
                                                    {v.code}
                                                </div>
                                            </TableCell>
                                            <TableCell className={`text-sm font-semibold ${
                                                v.stripe_color === 'red' ? 'text-red-600'
                                                : v.stripe_color === 'gold' ? 'gold-gradient-text'
                                                : v.stripe_color === 'blue' ? 'text-blue-600'
                                                : 'text-emerald-600'
                                            }`}>
                                                ${v.value}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {v.reason_label}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {v.issued_by_name || '-'}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {(() => {
                                                    const isEmail = v.delivery_method === 'email' || v.delivery_method === 'email_and_sms';
                                                    // Webhook delivery_status (delivered/bounced/complained/opened) trumps send-time status.
                                                    const ds = v.delivery_status;
                                                    const ss = v.email_send_status;
                                                    // Priority: ds → ss
                                                    const effective = ds || ss;
                                                    const BADGE_MAP = {
                                                        delivered:  { cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', label: t('vouchers.emailDelivered', 'Delivered') },
                                                        opened:     { cls: 'bg-blue-500/10 text-blue-600 border-blue-500/30',          label: t('vouchers.emailOpened', 'Opened') },
                                                        bounced:    { cls: 'bg-red-500/10 text-red-600 border-red-500/30',             label: t('vouchers.emailBounced', 'Bounced') },
                                                        complained: { cls: 'bg-red-500/10 text-red-600 border-red-500/30',             label: t('vouchers.emailComplained', 'Complaint') },
                                                        sent:       { cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', label: t('vouchers.emailSent', 'Sent') },
                                                        failed:     { cls: 'bg-red-500/10 text-red-600 border-red-500/30',             label: t('vouchers.emailFailed', 'Failed') },
                                                        pending:    { cls: 'bg-amber-500/10 text-amber-700 border-amber-500/30',       label: t('vouchers.emailPending', 'Pending') },
                                                    };
                                                    const statusCfg = BADGE_MAP[effective] || null;
                                                    return (
                                                        <div className="flex flex-col gap-1" data-testid={`voucher-delivery-${v.code}`}>
                                                            <Badge variant="outline" className={`text-xs w-fit ${
                                                                isEmail
                                                                    ? 'bg-blue-500/10 text-blue-600 border-blue-500/30'
                                                                    : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                                                            }`}>
                                                                {isEmail ? t('vouchers.email') : t('vouchers.staffCard')}
                                                            </Badge>
                                                            {isEmail && v.delivery_email && (
                                                                <span
                                                                    className="text-xs text-muted-foreground font-mono truncate max-w-[180px]"
                                                                    title={v.delivery_email + (v.email_sent_at ? `\nSent: ${formatDate(v.email_sent_at)}` : '') + (v.email_resend_id ? `\nResend ID: ${v.email_resend_id}` : '') + (v.email_error ? `\nError: ${v.email_error}` : '')}
                                                                    data-testid={`voucher-email-${v.code}`}
                                                                >
                                                                    {v.delivery_email}
                                                                </span>
                                                            )}
                                                            {isEmail && statusCfg && (
                                                                <div className="flex items-center gap-1">
                                                                    <Badge variant="outline" className={`text-[10px] w-fit ${statusCfg.cls}`} data-testid={`voucher-email-status-${v.code}`}>
                                                                        {statusCfg.label}
                                                                    </Badge>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openTimeline(v.code)}
                                                                        className="text-[10px] text-muted-foreground hover:text-foreground underline decoration-dotted"
                                                                        data-testid={`voucher-timeline-${v.code}`}
                                                                        title={t('vouchers.viewTimeline', 'View delivery timeline')}
                                                                    >
                                                                        <Activity className="w-3 h-3 inline" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {v.redeemed_by_name || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={`text-xs ${cfg.className}`}>
                                                    {cfg.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {v.status === 'active' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-xs text-blue-500 hover:text-red-600 hover:bg-red-50 h-7 px-2"
                                                        onClick={() => setVoidTarget(v.code)}
                                                        data-testid={`voucher-void-${v.code}`}
                                                        data-insight-id="vouchers.action.void"
                                                    >
                                                        <Ban className="w-3 h-3 mr-1" />
                                                        {t('vouchers.voidVoucher')}
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <div className="text-sm text-muted-foreground">
                {t('vouchers.showingVouchers', { current: sortedVouchers.length, total: vouchers.length })}
            </div>

            {/* Void Confirmation Dialog */}
            <Dialog open={!!voidTarget} onOpenChange={(open) => { if (!open) setVoidTarget(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('vouchers.voidConfirmTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('vouchers.voidConfirmMessage')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVoidTarget(null)} disabled={voiding}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmVoid}
                            disabled={voiding}
                            data-testid="voucher-void-confirm"
                            data-insight-id="vouchers.action.void-confirm"
                            data-insight-label="Confirm void"
                        >
                            {voiding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />}
                            {t('vouchers.confirmVoid')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* v3.4.0 — Delivery Event Timeline Modal */}
            <Dialog open={!!timelineTarget} onOpenChange={(open) => { if (!open) { setTimelineTarget(null); setTimelineData(null); } }}>
                <DialogContent className="max-w-lg" data-testid="voucher-timeline-modal" data-insight-id="vouchers.drawer.audit-detail" data-insight-label="Voucher audit timeline">
                    <DialogHeader>
                        <DialogTitle>{t('vouchers.deliveryTimelineTitle', 'Delivery Timeline')}</DialogTitle>
                        <DialogDescription>
                            {t('vouchers.deliveryTimelineDesc', 'Chronological log of email delivery events for this voucher.')}
                            {timelineTarget && <span className="block mt-1 font-mono text-xs">{timelineTarget}</span>}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[50vh] overflow-y-auto pr-1">
                        {timelineLoading && (
                            <div className="flex items-center justify-center py-8 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                {t('common.loading', 'Loading...')}
                            </div>
                        )}
                        {!timelineLoading && timelineData && (
                            <div className="space-y-3">
                                {timelineData.delivery_email && (
                                    <div className="text-xs text-muted-foreground">
                                        <span className="font-semibold">{t('vouchers.recipient', 'Recipient')}:</span>{' '}
                                        <span className="font-mono">{timelineData.delivery_email}</span>
                                    </div>
                                )}
                                {timelineData.events && timelineData.events.length > 0 ? (
                                    <ol className="relative border-l-2 border-muted pl-4 space-y-3">
                                        {timelineData.events.map((e) => {
                                            const color = e.delivery_status === 'delivered' ? 'text-emerald-600'
                                                : e.delivery_status === 'opened' ? 'text-blue-600'
                                                : (e.delivery_status === 'bounced' || e.delivery_status === 'complained') ? 'text-red-600'
                                                : 'text-muted-foreground';
                                            return (
                                                <li key={e.id} className="ml-1" data-testid={`timeline-event-${e.id}`}>
                                                    <span className={`absolute -left-[7px] w-3 h-3 rounded-full bg-current ${color}`} />
                                                    <div className={`text-sm font-semibold ${color}`}>{e.event_type}</div>
                                                    <div className="text-xs text-muted-foreground">{formatDate(e.timestamp)}</div>
                                                    {e.recipient_email && (
                                                        <div className="text-xs text-muted-foreground font-mono">{e.recipient_email}</div>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ol>
                                ) : (
                                    <div className="text-sm text-muted-foreground py-4 text-center">
                                        {t('vouchers.noTimelineEvents', 'No delivery events recorded yet.')}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setTimelineTarget(null); setTimelineData(null); }} data-testid="voucher-timeline-close">
                            {t('common.close', 'Close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/** Map known backend error strings to i18n keys */
function translateBackendError(detail, t) {
    if (!detail) return '';
    const map = {
        'ALREADY REDEEMED': t('vouchers.alreadyRedeemed'),
        'EXPIRED': t('vouchers.resultExpired'),
        'VOIDED': t('vouchers.statusVoided'),
        'Invalid PIN': t('vouchers.invalidPin'),
        'Voucher not found': t('vouchers.voucherNotFound'),
        'Voucher is not active': t('vouchers.statusExpired'),
    };
    return map[detail] || detail;
}
